import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { ArrowUp, CheckCircle, ChevronDown, Clock, ExternalLink, Eye, Globe, Loader2, Mail, MapPin, MessageCircle, Shield, Sparkles, Users, BarChart3, Lock, TrendingUp, Bell, Volume2, UserCog, DollarSign, FlaskConical } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { NOTIFICATION_SOUNDS, playNotificationSound, getStoredSound, setStoredSound } from "../../utils/notificationSounds";
import AdminPricingTab from "./AdminPricingTab";
import AdminEshopTiersTab from "./AdminEshopTiersTab";
import AdminBehaviorTab from "./AdminBehaviorTab";
import AdminAdsTab from "./AdminAdsTab";
import AdminFbAdsTab from "./AdminFbAdsTab";
import AdminFunnelTab from "./AdminFunnelTab";
import AdminReferralTab from "./AdminReferralTab";
import AdminUsersTab from "./AdminUsersTab";
import AdminABStatsTab from "./AdminABStatsTab";
import AdminDripABTab from "./AdminDripABTab";
import AdminCinematicAdsTab from "./AdminCinematicAdsTab";
import AdminVisionaryCopyTab from "./AdminVisionaryCopyTab";
import AdminSeoIndexingTab from "./AdminSeoIndexingTab";
import AdminFaqTab from "./AdminFaqTab";
import AdminChatFeedbackTab from "./AdminChatFeedbackTab";
import AdminAgentDemandTab from "./AdminAgentDemandTab";
import AdminAgentsTab from "./AdminAgentsTab";
import AdminFunnelsTab from "./AdminFunnelsTab";
import AdminJendralStoryTab from "./AdminJendralStoryTab";
import AdminCaseStudiesTab from "./AdminCaseStudiesTab";
import AdminRoadmapTab from "./AdminRoadmapTab";
import AdminInvoicingTab from "./AdminInvoicingTab";
import AdminAdStudioTab from "./AdminAdStudioTab";
import AdminAdCreativeStudioTab from "./AdminAdCreativeStudioTab";
import AdminLaunchChecklistTab from "./AdminLaunchChecklistTab";
import AdminAntiRankPillTab from "./AdminAntiRankPillTab";
import AdminLeadsExportTab from "./AdminLeadsExportTab";
import AdminGscSetupTab from "./AdminGscSetupTab";
import AdminSitemapPingTab from "./AdminSitemapPingTab";
import AdminSeedProductionTab from "./AdminSeedProductionTab";
import AdminStudioAutopilotTab from "./AdminStudioAutopilotTab";
import AdminMikaAdsTab from "./AdminMikaAdsTab";
import AdminDiagnosticFunnelTab from "./AdminDiagnosticFunnelTab";
import AdminManifestABTab from "./AdminManifestABTab";
import AdminHeroABTab from "./AdminHeroABTab";
import AdminIndexNowTab from "./AdminIndexNowTab";
import AdminFbAdCampaignsTab from "./AdminFbAdCampaignsTab";
import AdminChallengeTab from "./AdminChallengeTab";
import AdminPulseAITab from "./AdminPulseAITab";
import AdminThemeTab from "./AdminThemeTab";
import AdminUrlAnalyticsTab from "./AdminUrlAnalyticsTab";
import AdminPersonaAdsTab from "./AdminPersonaAdsTab";
import AdminFunnelCampaignsTab from "./AdminFunnelCampaignsTab";
import AdminFunnelSummaryTab from "./AdminFunnelSummaryTab";
import { Toaster } from "sonner";
import AdminGroupedTabs from "./AdminGroupedTabs";

export const AdminDashboard = ({ t, language, onBack }) => {
  const [activeTab, setActiveTab] = useState(() => {
    // V198 — Deep-link support: ?studio_kind=competitor_killer → open Studio tab
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      if (p.get("studio_kind") || p.get("studio_preset")) {
        return "ad_creative_studio";
      }
    }
    return "leads";
  });
  const [leads, setLeads] = useState({ leads: [], stats: {}, total: 0 });
  const [chatLogs, setChatLogs] = useState({ logs: [], stats: {}, total: 0 });
  const [discounts, setDiscounts] = useState({ codes: [], stats: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [gradeFilter, setGradeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [usedFilter, setUsedFilter] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [emailQueue, setEmailQueue] = useState({ emails: [], total: 0, pending: 0 });
  const [emailLog, setEmailLog] = useState({ emails: [], total: 0, sent: 0, queued: 0, by_type: {} });
  const [emailPreview, setEmailPreview] = useState(null);
  const [rescans, setRescans] = useState({ schedules: [], total: 0 });
  const [seoSettings, setSeoSettings] = useState({
    site_title: 'TopRankPulse — AI SEO Visibility Platform',
    site_description: 'Track if ChatGPT, Perplexity & Gemini cite your website. 18 tools, 26 languages.',
    default_language: 'en',
    hreflang_enabled: true,
    sitemap_enabled: true,
    geo_target_countries: [],
    local_business_name: 'TopRankPulse',
    local_business_type: 'SoftwareApplication',
    schema_enabled: true,
  });
  const [seoSaving, setSeoSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [securityEvents, setSecurityEvents] = useState({ events: [], total: 0 });
  const [socialSettings, setSocialSettings] = useState({ enabled: true, interval: 8000, types: ["scan","order","share","register"] });
  const [adCampaigns, setAdCampaigns] = useState([]);
  const [adStats, setAdStats] = useState(null);
  const [adCountryStats, setAdCountryStats] = useState(null);
  const [selectedAdLang, setSelectedAdLang] = useState("sk");
  const [pushNotifs, setPushNotifs] = useState({ notifications: [], unread: 0, total: 0 });
  const [waitlist, setWaitlist] = useState({ entries: [], total: 0, by_tool: {} });
  const [affiliatesData, setAffiliatesData] = useState({
    affiliates: [], conversions: [],
    totals: { count: 0, pending_eur: 0, paid_eur: 0, earned_eur: 0 },
  });
  const [notifSound, setNotifSound] = useState(getStoredSound());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (activeTab === 'leads' && gradeFilter) params.grade = gradeFilter;
        if (activeTab === 'chats' && statusFilter) params.status = statusFilter;

        const urls = {
          leads: `${API}/leads?limit=50${gradeFilter ? `&grade=${gradeFilter}` : ''}`,
          chats: `${API}/admin/chat-logs?limit=50${statusFilter ? `&status=${statusFilter}` : ''}`,
          discounts: `${API}/admin/discount-codes?limit=50${usedFilter !== '' ? `&used=${usedFilter}` : ''}`,
          emails: `${API}/notifications/queue?limit=50`,
          rescans: `${API}/rescan/schedules`,
        };

        if (urls[activeTab]) {
          const res = await axios.get(urls[activeTab]);
          if (activeTab === 'leads') setLeads(res.data);
          if (activeTab === 'chats') setChatLogs(res.data);
          if (activeTab === 'discounts') setDiscounts(res.data);
          if (activeTab === 'rescans') setRescans(res.data);
        }
        if (activeTab === 'seo') {
          try {
            const seoRes = await axios.get(`${API}/admin/seo-settings`);
            if (seoRes.data) setSeoSettings(prev => ({...prev, ...seoRes.data}));
          } catch {}
        }
        if (activeTab === 'analytics') {
          try {
            const [analyticsRes, shareRes, counterRes] = await Promise.all([
              axios.get(`${API}/admin/analytics`),
              axios.get(`${API}/share/stats`),
              axios.get(`${API}/stats/counter`),
            ]);
            setAnalytics({
              ...analyticsRes.data,
              shares: shareRes.data,
              counter: counterRes.data,
            });
          } catch {}
        }
        if (activeTab === 'security') {
          try {
            const secRes = await axios.get(`${API}/admin/security-events?limit=50`);
            setSecurityEvents(secRes.data || { events: [], total: 0 });
          } catch {
            setSecurityEvents({ events: [], total: 0 });
          }
        }
        if (activeTab === 'social') {
          try {
            const r = await axios.get(`${API}/social-proof/settings`);
            if (r.data) setSocialSettings(prev => ({...prev, ...r.data}));
          } catch {}
        }
        if (activeTab === 'ads') {
          try {
            const [campRes, statsRes, countryRes] = await Promise.all([
              axios.get(`${API}/ads/campaigns`),
              axios.get(`${API}/ads/stats/weekly`),
              axios.get(`${API}/ads/stats/by-country`),
            ]);
            setAdCampaigns(campRes.data.campaigns || []);
            setAdStats(statsRes.data);
            setAdCountryStats(countryRes.data);
          } catch {}
        }
        if (activeTab === 'emailing') {
          try {
            const emailRes = await axios.get(`${API}/admin/email-log?limit=50`);
            setEmailLog(emailRes.data);
          } catch {}
        }
        if (activeTab === 'notifs') {
          try {
            const notifRes = await axios.get(`${API}/push-notifications?limit=50`);
            setPushNotifs(notifRes.data);
          } catch {}
        }
        if (activeTab === 'waitlist') {
          const adminEmail = JSON.parse(localStorage.getItem("user") || "{}").email || "admin@toprankpulse.com";
          const headers = { "X-User-Email": adminEmail };
          try {
            const wlRes = await axios.get(`${API}/admin/funnel-tools/waitlist?limit=200`, { headers });
            setWaitlist(wlRes.data || { entries: [], total: 0, by_tool: {} });
          } catch {
            setWaitlist({ entries: [], total: 0, by_tool: {} });
          }
        }
        if (activeTab === 'affiliates') {
          const adminEmail = JSON.parse(localStorage.getItem("user") || "{}").email || "admin@toprankpulse.com";
          const headers = { "X-User-Email": adminEmail };
          try {
            const [affRes, convRes] = await Promise.all([
              axios.get(`${API}/admin/affiliates?limit=200`, { headers }),
              axios.get(`${API}/admin/affiliate-conversions?status=pending&limit=200`, { headers }),
            ]);
            setAffiliatesData({
              affiliates: affRes.data?.affiliates || [],
              conversions: convRes.data?.conversions || [],
              totals: affRes.data?.totals || { count: 0, pending_eur: 0, paid_eur: 0, earned_eur: 0 },
            });
          } catch {
            setAffiliatesData({ affiliates: [], conversions: [], totals: { count: 0, pending_eur: 0, paid_eur: 0, earned_eur: 0 } });
          }
        }
      } catch (e) {
        console.error('Admin fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab, gradeFilter, statusFilter, usedFilter]);

  const handleMarkReviewed = async (messageId) => {
    try {
      await axios.put(`${API}/admin/chat-logs/${messageId}?status=reviewed`);
      setChatLogs(prev => ({
        ...prev,
        logs: prev.logs.map(l => l.message_id === messageId ? { ...l, status: 'reviewed' } : l),
        stats: {
          ...prev.stats,
          by_status: {
            ...prev.stats.by_status,
            new: Math.max(0, (prev.stats.by_status?.new || 1) - 1),
            reviewed: (prev.stats.by_status?.reviewed || 0) + 1
          }
        }
      }));
    } catch (e) {
      console.error('Update error:', e);
    }
  };

  const handleExport = async () => {
    try {
      const endpoints = { leads: '/leads/export', chats: '/admin/chat-logs/export', discounts: '/admin/discount-codes' };
      const res = await axios.get(`${API}${endpoints[activeTab]}`);
      const data = res.data.data || res.data.codes || [];
      const csv = data.length ? [Object.keys(data[0]).join(','), ...data.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n') : '';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${activeTab}_export.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    }
  };

  const handleSaveSeo = async () => {
    setSeoSaving(true);
    try {
      await axios.post(`${API}/admin/seo-settings`, seoSettings);
    } catch (e) { console.error(e); }
    setSeoSaving(false);
  };

  const gradeColors = { A: 'bg-green-100 text-green-800', B: 'bg-blue-100 text-blue-800', C: 'bg-yellow-100 text-yellow-800', D: 'bg-orange-100 text-orange-800', F: 'bg-red-100 text-red-800' };
  const statusColors = { new: 'bg-blue-100 text-blue-700', reviewed: 'bg-green-100 text-green-700', used_for_improvement: 'bg-purple-100 text-purple-700' };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen bg-gray-50 admin-root" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-gray-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={onBack} className="flex items-center gap-1 text-xs sm:text-sm text-gray-300 hover:text-white transition-colors shrink-0" data-testid="admin-back-btn">
              <ArrowUp className="w-4 h-4 rotate-[-90deg]" />
              <span className="hidden sm:inline">{t.admin_back}</span>
            </button>
            <div className="w-px h-6 bg-gray-700 shrink-0" />
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="w-5 h-5 text-blue-400 shrink-0" />
              <span className="font-bold font-['Chivo'] text-sm sm:text-base truncate">{t.admin_title}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={`${API}/sitemap-export/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-master-catalog-pdf-btn"
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-purple-600 hover:bg-purple-500 rounded text-xs sm:text-sm transition-colors"
              title="Stiahnuť kompletný PDF prehľad celej platformy (všetky stránky, tools, pricing, integrácie, changelog)"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Master Overview</span> PDF
            </a>
            <a
              href={`${API}/sitemap-export/funnel-flow`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-funnel-flow-pdf-btn"
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-purple-700/80 hover:bg-purple-600 rounded text-xs sm:text-sm transition-colors"
              title="Vizuálny funnel flow diagram (1 strana landscape) — Acquisition → Tripwire → Core → Ascending + back-end loops"
            >
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Funnel Flow</span> PDF
            </a>
            <a
              href={`${API}/sitemap-export/affiliate-fact-sheet.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-affiliate-factsheet-pdf-btn"
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-[#FFE600]/15 hover:bg-[#FFE600]/25 text-[#FFE600] border border-[#FFE600]/40 rounded text-xs sm:text-sm transition-colors"
              title="1-stranový Affiliate Program Fact Sheet — attachment k AffiliatePort.eu / Awin / Impact submission forms (live stats)"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Affiliate</span> Fact Sheet
            </a>
            <a
              href={`${API}/sitemap-export/product-hunt-kit.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-ph-kit-pdf-btn"
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-[#DA552F]/15 hover:bg-[#DA552F]/25 text-[#DA552F] border border-[#DA552F]/40 rounded text-xs sm:text-sm transition-colors"
              title="Product Hunt Launch Kit — countdown, maker comment, 5 tweetov, hunter outreach email, cross-post copy (live metrics)"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">PH Launch</span> Kit
            </a>
            <button onClick={handleExport} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded text-xs sm:text-sm transition-colors" data-testid="admin-export-btn">
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">{t.admin_export}</span> CSV
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Tabs — grouped dropdown menu: cleaner than a 21-chip grid.
            On desktop: 4 group pills that open a tabbed popover.
            On mobile: same groups but stacked. Includes a search filter.
            All old test-ids (`admin-tab-${key}`) are preserved so backend
            tests don't break. */}
        <AdminGroupedTabs
          t={t}
          activeTab={activeTab}
          onSelect={setActiveTab}
          counts={{
            leads: leads.stats.total || 0,
            chats: chatLogs.stats.total || 0,
            discounts: discounts.stats.total || 0,
            emails: emailLog.total || 0,
            rescans: rescans.total || 0,
            ads: adCampaigns.length,
            notifs: pushNotifs.unread || 0,
            waitlist: waitlist.total || 0,
            affiliates: affiliatesData.totals?.count || 0,
            security: securityEvents.total || 0,
            brand: 3, pulseai: 9, theme: 4, fbig_ads: 10,
          }}
        />

        {/* === LEADS TAB === */}
        {activeTab === 'leads' && (
          <div data-testid="admin-leads-panel">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_total}</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{leads.stats.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_hot_leads}</p>
                <p className="text-3xl font-mono font-bold text-orange-600 mt-1">{leads.stats.hot_leads || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_avg_score}</p>
                <p className="text-3xl font-mono font-bold text-blue-600 mt-1">{leads.stats.average_score || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_grade}</p>
                <div className="flex gap-2 mt-2">
                  {['A', 'B', 'C', 'D', 'F'].map(g => (
                    <span key={g} className={`px-2 py-0.5 text-xs font-bold rounded ${gradeColors[g]}`}>{g}: {leads.stats.by_grade?.[g] || 0}</span>
                  ))}
                </div>
              </div>
            </div>
            {/* Filter */}
            <div className="flex gap-2 mb-4">
              {['', 'A', 'B', 'C', 'D', 'F'].map(g => (
                <button key={g} data-testid={`filter-grade-${g || 'all'}`} onClick={() => setGradeFilter(g)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${gradeFilter === g ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {g || t.admin_filter_all}
                </button>
              ))}
            </div>
            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
              ) : leads.leads.length === 0 ? (
                <div className="p-12 text-center text-gray-400">{t.admin_no_data}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_email}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_url}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_score}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_grade}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_language}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_date}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.leads.map((lead, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`lead-row-${i}`}>
                          <td className="px-4 py-3 font-medium">{lead.email}</td>
                          <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{lead.url}</td>
                          <td className="px-4 py-3 text-center font-mono font-bold">{lead.lead_score}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2.5 py-1 rounded text-xs font-bold ${gradeColors[lead.lead_grade] || 'bg-gray-100'}`}>{lead.lead_grade}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-xs uppercase">{lead.language}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(lead.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === CHAT LOGS TAB === */}
        {activeTab === 'chats' && (
          <div data-testid="admin-chats-panel">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_total}</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{chatLogs.stats.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_new}</p>
                <p className="text-3xl font-mono font-bold text-blue-600 mt-1">{chatLogs.stats.by_status?.new || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_reviewed}</p>
                <p className="text-3xl font-mono font-bold text-green-600 mt-1">{chatLogs.stats.by_status?.reviewed || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_language}</p>
                <div className="flex gap-1 mt-2 flex-wrap">
                  {Object.entries(chatLogs.stats.by_language || {}).map(([lang, count]) => (
                    <span key={lang} className="px-2 py-0.5 bg-gray-100 text-xs font-medium rounded">{lang}: {count}</span>
                  ))}
                </div>
              </div>
            </div>
            {/* Filter */}
            <div className="flex gap-2 mb-4">
              {['', 'new', 'reviewed'].map(s => (
                <button key={s} data-testid={`filter-status-${s || 'all'}`} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${statusFilter === s ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {s === '' ? t.admin_filter_all : s === 'new' ? t.admin_new : t.admin_reviewed}
                </button>
              ))}
            </div>
            {/* Chat Log Cards */}
            <div className="space-y-3">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
              ) : chatLogs.logs.length === 0 ? (
                <div className="p-12 text-center text-gray-400">{t.admin_no_data}</div>
              ) : chatLogs.logs.map((log, i) => (
                <div key={log.message_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`chat-log-${i}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100 cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === log.message_id ? null : log.message_id)}>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${statusColors[log.status] || 'bg-gray-100 text-gray-600'}`}>{log.status}</span>
                      <span className="text-xs text-gray-500 uppercase">{log.original_language}</span>
                      <span className="text-sm font-medium text-gray-800 truncate max-w-[400px]">{log.question_sk || log.question_original}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">{formatDate(log.timestamp)}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedLog === log.message_id ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {expandedLog === log.message_id && (
                    <div className="p-4 space-y-3 animate-fade-in">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t.admin_question} (SK)</p>
                        <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded">{log.question_sk || log.question_original}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t.admin_answer} (SK)</p>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded whitespace-pre-wrap max-h-48 overflow-y-auto">{log.answer_sk || log.answer_original}</p>
                      </div>
                      {log.email && <p className="text-xs text-gray-500">{t.admin_email}: {log.email}</p>}
                      {log.discount_code && <p className="text-xs text-purple-600 font-medium">{t.admin_code}: {log.discount_code}</p>}
                      {log.status === 'new' && (
                        <button onClick={() => handleMarkReviewed(log.message_id)} data-testid={`mark-reviewed-${i}`}
                          className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded hover:bg-green-700 transition-colors">
                          <CheckCircle className="w-3.5 h-3.5" />
                          {t.admin_mark_reviewed}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* === DISCOUNT CODES TAB === */}
        {activeTab === 'discounts' && (
          <div data-testid="admin-discounts-panel">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_total}</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{discounts.stats.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_used}</p>
                <p className="text-3xl font-mono font-bold text-green-600 mt-1">{discounts.stats.used || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_unused}</p>
                <p className="text-3xl font-mono font-bold text-gray-500 mt-1">{discounts.stats.unused || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t.admin_conversion}</p>
                <p className="text-3xl font-mono font-bold text-blue-600 mt-1">{discounts.stats.conversion_rate || 0}%</p>
              </div>
            </div>
            {/* Filter */}
            <div className="flex gap-2 mb-4">
              {['', 'false', 'true'].map(u => (
                <button key={u} data-testid={`filter-used-${u || 'all'}`} onClick={() => setUsedFilter(u)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${usedFilter === u ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {u === '' ? t.admin_filter_all : u === 'true' ? t.admin_used : t.admin_unused}
                </button>
              ))}
            </div>
            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
              ) : discounts.codes.length === 0 ? (
                <div className="p-12 text-center text-gray-400">{t.admin_no_data}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_code}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_type}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_discount}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_email}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_status}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_expires}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discounts.codes.map((code, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`discount-row-${i}`}>
                          <td className="px-4 py-3 font-mono font-bold text-sm">{code.code}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">{code.type}</span>
                          </td>
                          <td className="px-4 py-3 text-center font-mono font-bold text-green-600">{code.discount_percent}%</td>
                          <td className="px-4 py-3 text-gray-600">{code.email || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2.5 py-1 rounded text-xs font-bold ${code.used ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {code.used ? t.admin_used : t.admin_unused}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(code.expires_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === EMAIL QUEUE TAB === */}
        {activeTab === 'emails' && (
          <div data-testid="admin-emails-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Celkom emailov</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{emailLog.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-emerald-200 p-4">
                <p className="text-xs font-medium text-emerald-600 uppercase">Odoslané</p>
                <p className="text-3xl font-mono font-bold text-emerald-600 mt-1">{emailLog.sent || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-orange-200 p-4">
                <p className="text-xs font-medium text-orange-600 uppercase">Vo fronte</p>
                <p className="text-3xl font-mono font-bold text-orange-600 mt-1">{emailLog.queued || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-blue-200 p-4">
                <p className="text-xs font-medium text-blue-600 uppercase">RESEND API</p>
                <p className="text-lg font-bold text-blue-700 mt-1">{emailLog.sent > 0 ? 'Aktívne' : 'Režim fronty'}</p>
                <p className="text-xs text-blue-500">{emailLog.sent > 0 ? 'Emails being sent' : 'Pridajte RESEND_API_KEY pre odosielanie'}</p>
              </div>
            </div>

            {/* Email Types */}
            {Object.keys(emailLog.by_type || {}).length > 0 && (
              <div className="flex gap-2 mb-4 flex-wrap">
                {Object.entries(emailLog.by_type).map(([type, count]) => (
                  <span key={type} className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600">
                    {type}: <span className="font-bold text-gray-900">{count}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Email Preview */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Náhľad email šablón</h3>
              <div className="flex gap-2 flex-wrap">
                {['welcome','payment_confirmed','scan_results','score_drop','weekly_report','blog_complete'].map(type => (
                  <button key={type} onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/admin/email-preview/${type}/sk`);
                      setEmailPreview(r.data);
                    } catch {}
                  }} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-xs font-medium rounded transition-colors" data-testid={`preview-${type}`}>
                    {type.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
              {emailPreview && (
                <div className="mt-4 border border-gray-200 rounded overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                    <span className="text-xs font-mono text-gray-500">{emailPreview.type} ({emailPreview.language})</span>
                    <span className="text-sm font-bold text-gray-700">{emailPreview.subject}</span>
                  </div>
                  <iframe srcDoc={emailPreview.html} className="w-full h-[400px] border-0" title="Email Preview" />
                </div>
              )}
            </div>

            {/* Email Log Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Log emailov</h3>
              </div>
              {!emailLog.emails?.length ? (
                <div className="p-12 text-center text-gray-400">Zatiaľ žiadne emaily. Objavia sa tu po registrácii používateľa alebo SEO skenoch.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Komu</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Predmet</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Typ</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Jazyk</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLog.emails.map((em, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`email-row-${i}`}>
                        <td className="px-4 py-3 font-medium text-gray-800">{em.to}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-[250px] truncate">{em.subject}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            em.type === 'welcome' ? 'bg-emerald-100 text-emerald-700' :
                            em.type === 'payment_confirmed' ? 'bg-blue-100 text-blue-700' :
                            em.type === 'score_drop' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{em.type}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-xs font-mono text-gray-500">{em.language?.toUpperCase()}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2.5 py-1 rounded text-xs font-bold ${em.sent ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                            {em.sent ? 'Sent' : 'Queued'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(em.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* === RESCANS TAB === */}
        {activeTab === 'rescans' && (
          <div data-testid="admin-rescans-panel">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
              ) : !rescans.schedules?.length ? (
                <div className="p-12 text-center text-gray-400">{t.admin_no_data}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_url}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">{t.admin_email}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.rescan_frequency}</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">Scans</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">{t.admin_status}</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Next Scan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rescans.schedules.map((rs, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`rescan-row-${i}`}>
                          <td className="px-4 py-3 font-medium max-w-[250px] truncate">{rs.url}</td>
                          <td className="px-4 py-3 text-gray-600">{rs.email || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">{rs.frequency}</span>
                          </td>
                          <td className="px-4 py-3 text-center font-mono">{rs.scan_count || 0}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2.5 py-1 rounded text-xs font-bold ${rs.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {rs.active ? 'Aktívne' : 'Paused'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(rs.next_scan)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === SEO / GEO TAB === */}
        {activeTab === 'seo' && (
          <div data-testid="admin-seo-panel">
            <div className="grid md:grid-cols-2 gap-6">
              {/* SEO Settings */}
              <div className="bg-[#141414] border border-zinc-800 p-6">
                <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-[#FF3333]" />SEO Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Site Title (meta)</label>
                    <input value={seoSettings.site_title} onChange={e => setSeoSettings(p => ({...p, site_title: e.target.value}))} className="w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-white text-sm focus:border-[#FF3333] focus:outline-none" data-testid="seo-site-title" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Site Description (meta)</label>
                    <textarea value={seoSettings.site_description} onChange={e => setSeoSettings(p => ({...p, site_description: e.target.value}))} rows={3} className="w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-white text-sm focus:border-[#FF3333] focus:outline-none" data-testid="seo-site-desc" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Default Language</label>
                    <select value={seoSettings.default_language} onChange={e => setSeoSettings(p => ({...p, default_language: e.target.value}))} className="w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-white text-sm focus:border-[#FF3333] focus:outline-none">
                      {['en','sk','cs','de','fr','es','pt','it','pl','hu','nl','sv','no','da','fi','ro','bg','hr','uk','tr','ja','ko','zh','hi','ar','id'].map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={seoSettings.hreflang_enabled} onChange={e => setSeoSettings(p => ({...p, hreflang_enabled: e.target.checked}))} className="accent-[#FF3333]" />
                    <span className="text-sm text-zinc-300">Enable hreflang tags (multi-language SEO)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={seoSettings.sitemap_enabled} onChange={e => setSeoSettings(p => ({...p, sitemap_enabled: e.target.checked}))} className="accent-[#FF3333]" />
                    <span className="text-sm text-zinc-300">Generate XML sitemap for all languages</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={seoSettings.schema_enabled} onChange={e => setSeoSettings(p => ({...p, schema_enabled: e.target.checked}))} className="accent-[#FF3333]" />
                    <span className="text-sm text-zinc-300">Enable Schema.org structured data</span>
                  </div>
                </div>
              </div>

              {/* GEO Settings */}
              <div className="bg-[#141414] border border-zinc-800 p-6">
                <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#FFE600]" />GEO Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Business Name</label>
                    <input value={seoSettings.local_business_name} onChange={e => setSeoSettings(p => ({...p, local_business_name: e.target.value}))} className="w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-white text-sm focus:border-[#FF3333] focus:outline-none" data-testid="seo-business-name" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Business Type</label>
                    <select value={seoSettings.local_business_type} onChange={e => setSeoSettings(p => ({...p, local_business_type: e.target.value}))} className="w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-white text-sm focus:border-[#FF3333] focus:outline-none">
                      {['SoftwareApplication','LocalBusiness','Organization','WebSite','Product','Service','Store','Restaurant'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Target Countries (comma separated)</label>
                    <input value={(seoSettings.geo_target_countries || []).join(', ')} onChange={e => setSeoSettings(p => ({...p, geo_target_countries: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} placeholder="SK, CZ, DE, US, GB..." className="w-full px-3 py-2 bg-[#0a0a0a] border border-zinc-700 text-white text-sm focus:border-[#FF3333] focus:outline-none" data-testid="seo-countries" />
                  </div>
                </div>

                <div className="mt-6 p-4 bg-[#0a0a0a] border border-zinc-700">
                  <p className="text-zinc-400 text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2">Active Languages</p>
                  <div className="flex flex-wrap gap-1">
                    {['🇬🇧 EN','🇸🇰 SK','🇨🇿 CS','🇩🇪 DE','🇵🇱 PL','🇭🇺 HU','🇫🇷 FR','🇪🇸 ES','🇧🇷 PT','🇮🇹 IT','🇳🇱 NL','🇸🇪 SV','🇳🇴 NO','🇩🇰 DA','🇫🇮 FI','🇷🇴 RO','🇧🇬 BG','🇭🇷 HR','🇺🇦 UK','🇹🇷 TR','🇯🇵 JA','🇰🇷 KO','🇨🇳 ZH','🇮🇳 HI','🇸🇦 AR','🇮🇩 ID'].map((l,i) => (
                      <span key={i} className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs border border-zinc-700">{l}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button onClick={handleSaveSeo} disabled={seoSaving} className="mt-6 px-6 py-3 bg-[#FF3333] text-white font-bold hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="save-seo-btn">
              {seoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Save SEO & GEO Settings
            </button>
          </div>
        )}

        {/* === ANALYTICS TAB === */}
        {activeTab === 'analytics' && (
          <div data-testid="admin-analytics-panel">
            {!analytics ? (
              <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Celkom používateľov</p>
                    <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{analytics.users?.total || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Blogové články</p>
                    <p className="text-3xl font-mono font-bold text-blue-600 mt-1">{analytics.blog?.total_posts || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Zdieľania</p>
                    <p className="text-3xl font-mono font-bold text-purple-600 mt-1">{analytics.shares?.total_shares || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Počítadlo skenov</p>
                    <p className="text-3xl font-mono font-bold text-emerald-600 mt-1">{(analytics.counter?.count || 12519).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Bezpečnosť (24h)</p>
                    <p className="text-3xl font-mono font-bold text-orange-600 mt-1">{analytics.security_events_24h || 0}</p>
                  </div>
                </div>

                {/* Share Platform Stats */}
                {analytics.shares?.total_shares > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Zdieľania podľa platforiem</h3>
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(analytics.shares?.by_platform || {}).map(([platform, count]) => (
                        <span key={platform} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                          <span className="text-xs text-gray-500">{platform}: </span>
                          <span className="text-sm font-bold text-gray-900">{count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scans Per Day Chart */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Skeny za deň (posledných 30 dní)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.scans_per_day || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{fontSize: 10}} />
                      <YAxis tick={{fontSize: 11}} />
                      <Tooltip />
                      <Bar dataKey="scans" fill="#FF3333" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Score Distribution */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Distribúcia skóre</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={analytics.score_distribution || []} dataKey="count" nameKey="grade" cx="50%" cy="50%" outerRadius={80} label={({grade, count}) => `${grade}: ${count}`}>
                          {(analytics.score_distribution || []).map((_, i) => (
                            <Cell key={i} fill={['#10B981','#3B82F6','#F59E0B','#F97316','#EF4444'][i]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* User Plans */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Používatelia podľa plánu</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={Object.entries(analytics.users?.by_plan || {}).map(([k,v]) => ({name: k, value: v}))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({name, value}) => `${name}: ${value}`}>
                          {Object.keys(analytics.users?.by_plan || {}).map((_, i) => (
                            <Cell key={i} fill={['#6B7280','#FF3333','#FFE600','#10B981'][i]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Languages */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Top jazyky</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.top_languages || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{fontSize: 11}} />
                        <YAxis type="category" dataKey="lang" tick={{fontSize: 11}} width={40} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3B82F6" radius={[0,4,4,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top Domains */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Najskenovanejšie domény</h3>
                    <div className="space-y-2">
                      {(analytics.top_domains || []).map((d, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-gray-400 w-5">{i+1}.</span>
                            <span className="text-sm font-medium text-gray-800 truncate max-w-[200px]">{d.domain}</span>
                          </div>
                          <span className="text-sm font-mono font-bold text-gray-600">{d.count}x</span>
                        </div>
                      ))}
                      {(!analytics.top_domains || analytics.top_domains.length === 0) && (
                        <p className="text-gray-400 text-sm text-center py-4">Zatiaľ žiadne dáta</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}


        {/* === ADS TAB === */}
        {activeTab === 'ads' && (
          <div data-testid="admin-ads-panel">
            {/* Weekly Stats */}
            {adStats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Zobrazenia</p>
                    <p className="text-2xl font-mono font-bold text-gray-900 mt-1">{adStats.summary?.impressions?.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-blue-200 p-4">
                    <p className="text-xs font-medium text-blue-600 uppercase">Kliky</p>
                    <p className="text-2xl font-mono font-bold text-blue-600 mt-1">{adStats.summary?.clicks?.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-emerald-200 p-4">
                    <p className="text-xs font-medium text-emerald-600 uppercase">Konverzie</p>
                    <p className="text-2xl font-mono font-bold text-emerald-600 mt-1">{adStats.summary?.conversions}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">CTR</p>
                    <p className="text-2xl font-mono font-bold text-gray-900 mt-1">{adStats.summary?.ctr}%</p>
                  </div>
                  <div className="bg-white rounded-lg border border-orange-200 p-4">
                    <p className="text-xs font-medium text-orange-600 uppercase">Náklady</p>
                    <p className="text-2xl font-mono font-bold text-orange-600 mt-1">€{adStats.summary?.spend}</p>
                  </div>
                </div>

                {/* Weekly Chart */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Týždenné štatistiky</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={adStats.daily || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{fontSize: 10}} />
                      <YAxis tick={{fontSize: 11}} />
                      <Tooltip />
                      <Bar dataKey="clicks" fill="#3B82F6" radius={[4,4,0,0]} name="Kliky" />
                      <Bar dataKey="conversions" fill="#10B981" radius={[4,4,0,0]} name="Konverzie" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}

            {/* Country Stats */}
            {adCountryStats && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Štatistiky podľa krajín</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <th className="px-3 py-2 text-left">Krajina</th>
                      <th className="px-3 py-2 text-right">Zobrazenia</th>
                      <th className="px-3 py-2 text-right">Kliky</th>
                      <th className="px-3 py-2 text-right">Konverzie</th>
                      <th className="px-3 py-2 text-right">CTR</th>
                      <th className="px-3 py-2 text-right">Náklady</th>
                    </tr></thead>
                    <tbody>
                      {(adCountryStats.countries || []).map((c, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{c.name}</td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{c.impressions.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono text-blue-600">{c.clicks}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">{c.conversions}</td>
                          <td className="px-3 py-2 text-right font-mono text-gray-500">{c.ctr}%</td>
                          <td className="px-3 py-2 text-right font-mono text-orange-600">€{c.spend}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ad Campaigns with language tabs */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Reklamné kampane</h3>
                <span className="text-xs text-gray-400">{adCampaigns.length} kampane</span>
              </div>

              {/* Language selector */}
              <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
                {["sk","cs","de","pl","hu","en","fr","es","pt","it","nl","sv","no","da","fi","ro","bg","hr","uk","tr","ja","ko","zh","hi","ar","id"].map(lang => (
                  <button key={lang} onClick={() => setSelectedAdLang(lang)}
                    className={`px-3 py-1.5 text-xs font-bold whitespace-nowrap border transition-colors ${
                      selectedAdLang === lang ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                    }`}>{lang.toUpperCase()}</button>
                ))}
              </div>

              {/* Campaign cards */}
              <div className="space-y-8">
                {adCampaigns.map((campaign, ci) => {
                  const variant = campaign.variants?.[selectedAdLang] || campaign.variants?.["en"] || {};
                  const formats = [
                    { id: "fb-feed", label: "FB Feed", w: "w-full", ratio: "aspect-[1200/628]", px: "1200×628", platform: "Facebook" },
                    { id: "ig-square", label: "IG Post", w: "w-[320px]", ratio: "aspect-square", px: "1080×1080", platform: "Instagram" },
                    { id: "ig-story", label: "IG Story", w: "w-[200px]", ratio: "aspect-[9/16]", px: "1080×1920", platform: "Instagram" },
                    { id: "google", label: "Google Ads", w: "w-full", ratio: "aspect-[728/90]", px: "728×90", platform: "Google" },
                  ];
                  return (
                    <div key={ci} className="border border-gray-200 overflow-hidden" data-testid={`ad-campaign-${campaign.id}`}>
                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold text-sm text-gray-800">{campaign.name}</p>
                              {campaign.type && (
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${
                                  campaign.type === 'awareness' ? 'bg-blue-100 text-blue-700' :
                                  campaign.type === 'case_study' ? 'bg-emerald-100 text-emerald-700' :
                                  campaign.type === 'ugc' ? 'bg-purple-100 text-purple-700' :
                                  campaign.type === 'calculator' ? 'bg-amber-100 text-amber-700' :
                                  campaign.type === 'urgency' ? 'bg-red-100 text-red-700' :
                                  campaign.type === 'social_proof' ? 'bg-cyan-100 text-cyan-700' :
                                  'bg-gray-100 text-gray-600'
                                }`} data-testid={`campaign-type-${campaign.id}`}>{campaign.type?.replace('_', ' ')}</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400">Jazyk: {selectedAdLang.toUpperCase()} · {Object.keys(campaign.variants || {}).length} jazykov</p>
                          </div>
                          <button onClick={() => { navigator.clipboard.writeText(`${variant.headline}\n\n${variant.text}\n\n${variant.cta}\n\nhttps://toprankpulse.com`); }}
                            className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 hover:bg-gray-100" data-testid={`copy-btn-${campaign.id}`}>Kopírovať text</button>
                        </div>
                        {/* Strategy metadata */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {campaign.platforms?.map(p => (
                            <span key={p} className="px-2 py-0.5 text-[10px] font-medium bg-gray-900 text-white rounded-sm" data-testid={`platform-${campaign.id}-${p}`}>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
                          ))}
                          {campaign.recommended_objective && (
                            <span className="px-2 py-0.5 text-[10px] font-medium bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/20 rounded-sm" data-testid={`objective-${campaign.id}`}>
                              Cieľ: {campaign.recommended_objective}
                            </span>
                          )}
                          {campaign.recommended_placement && (
                            <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm" data-testid={`placement-${campaign.id}`}>
                              Placement: {campaign.recommended_placement}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Format previews */}
                      <div className="p-4 space-y-6">
                        {formats.map(fmt => (
                          <div key={fmt.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{fmt.label} <span className="font-normal text-gray-400">({fmt.px})</span></p>
                              <span className="text-[10px] bg-gray-100 px-2 py-0.5 text-gray-500">{fmt.platform}</span>
                            </div>
                            <div className={`${fmt.w} ${fmt.ratio} relative overflow-hidden bg-black border border-gray-200 mx-auto`}>
                              <img src={campaign.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                              {fmt.id === "google" ? (
                                <div className="absolute inset-0 bg-black/60 flex items-center px-4 gap-4">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white font-bold text-xs truncate">{variant.headline}</p>
                                    <p className="text-gray-300 text-[10px] truncate">{variant.text}</p>
                                  </div>
                                  <span className="px-3 py-1 bg-[#FF3333] text-white text-[10px] font-bold whitespace-nowrap flex-shrink-0">{variant.cta}</span>
                                </div>
                              ) : fmt.id === "ig-story" ? (
                                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80 flex flex-col justify-between p-4">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-[#FF3333] flex items-center justify-center"><TrendingUp className="w-3 h-3 text-white" /></div>
                                    <span className="text-white text-[10px] font-bold">TopRankPulse</span>
                                  </div>
                                  <div>
                                    <p className="text-white font-black text-sm leading-tight mb-1">{variant.headline}</p>
                                    <p className="text-gray-300 text-[9px] mb-2 line-clamp-2">{variant.text}</p>
                                    <span className="inline-block px-3 py-1.5 bg-[#FF3333] text-white text-[10px] font-bold">{variant.cta}</span>
                                  </div>
                                </div>
                              ) : fmt.id === "ig-square" ? (
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent flex flex-col justify-end p-5">
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="w-7 h-7 bg-[#FF3333] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
                                    <span className="text-white text-xs font-bold">TopRankPulse</span>
                                  </div>
                                  <p className="text-white font-black text-lg leading-tight mb-2">{variant.headline}</p>
                                  <p className="text-gray-300 text-xs mb-3 line-clamp-2">{variant.text}</p>
                                  <span className="inline-block px-4 py-2 bg-[#FF3333] text-white text-xs font-bold w-fit">{variant.cta}</span>
                                </div>
                              ) : (
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-6">
                                  <p className="text-white font-black text-xl leading-tight mb-2">{variant.headline}</p>
                                  <p className="text-gray-300 text-sm mb-3">{variant.text}</p>
                                  <div className="flex items-center gap-3">
                                    <span className="px-4 py-2 bg-[#FF3333] text-white text-sm font-bold">{variant.cta}</span>
                                    <span className="text-xs text-gray-400">toprankpulse.com</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* === SOCIAL PROOF TAB === */}
        {activeTab === 'social' && (
          <div data-testid="admin-social-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Stav</p>
                <p className={`text-lg font-bold mt-1 ${socialSettings.enabled ? 'text-emerald-600' : 'text-red-600'}`}>
                  {socialSettings.enabled ? 'Aktívne' : 'Vypnuté'}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Interval</p>
                <p className="text-2xl font-mono font-bold text-gray-900 mt-1">{socialSettings.interval / 1000}s</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Typy notifikácií</p>
                <p className="text-sm font-bold text-gray-700 mt-1">{(socialSettings.types || []).length} aktívnych</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Krajiny</p>
                <p className="text-lg font-bold text-gray-700 mt-1">16 krajín</p>
                <p className="text-xs text-gray-400">s reálnymi menami</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Nastavenia Social Proof</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Povoliť popup notifikácie</p>
                    <p className="text-xs text-gray-400">Zobrazovať social proof popupy na stránke</p>
                  </div>
                  <button
                    data-testid="social-toggle"
                    onClick={async () => {
                      const newVal = !socialSettings.enabled;
                      setSocialSettings(p => ({...p, enabled: newVal}));
                      await axios.put(`${API}/social-proof/settings?enabled=${newVal}&interval=${socialSettings.interval}&types=${(socialSettings.types||[]).join(',')}`);
                    }}
                    className={`w-12 h-6 rounded-full transition-colors ${socialSettings.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${socialSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                <div className="py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-800 mb-2">Interval medzi notifikáciami</p>
                  <div className="flex gap-2">
                    {[5,8,12,15,20].map(s => (
                      <button key={s}
                        onClick={async () => {
                          setSocialSettings(p => ({...p, interval: s * 1000}));
                          await axios.put(`${API}/social-proof/settings?enabled=${socialSettings.enabled}&interval=${s * 1000}&types=${(socialSettings.types||[]).join(',')}`);
                        }}
                        className={`px-4 py-2 text-sm font-medium border rounded ${socialSettings.interval === s * 1000 ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                        data-testid={`interval-${s}`}
                      >
                        {s}s
                      </button>
                    ))}
                  </div>
                </div>

                <div className="py-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">Typy notifikácií</p>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      {id:"scan", label:"Sken stránky", emoji:"🔍"},
                      {id:"order", label:"Objednávka", emoji:"🛒"},
                      {id:"share", label:"Zdieľanie", emoji:"📤"},
                      {id:"register", label:"Registrácia", emoji:"👤"},
                    ].map(t => {
                      const active = (socialSettings.types || []).includes(t.id);
                      return (
                        <button key={t.id}
                          onClick={async () => {
                            const newTypes = active
                              ? (socialSettings.types || []).filter(x => x !== t.id)
                              : [...(socialSettings.types || []), t.id];
                            setSocialSettings(p => ({...p, types: newTypes}));
                            await axios.put(`${API}/social-proof/settings?enabled=${socialSettings.enabled}&interval=${socialSettings.interval}&types=${newTypes.join(',')}`);
                          }}
                          className={`px-4 py-2 text-sm font-medium border rounded flex items-center gap-2 ${active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200'}`}
                          data-testid={`type-${t.id}`}
                        >
                          <span>{t.emoji}</span>{t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Náhľad notifikácií</h3>
              <div className="space-y-2">
                {[
                  {flag:"🇸🇰", name:"Peter", msg:"skenoval/a svoju stránku", time:"pred 23 sekundami", type:"scan"},
                  {flag:"🇩🇪", name:"Thomas", msg:"objednal/a Pro plán", time:"pred 2 minútami", type:"order"},
                  {flag:"🇬🇧", name:"Emily", msg:"zdieľal/a svoje SEO skóre", time:"práve teraz", type:"share"},
                  {flag:"🇯🇵", name:"Yuki", msg:"sa práve zaregistroval/a", time:"pred 45 sekundami", type:"register"},
                  {flag:"🇪🇸", name:"Carlos", msg:"objednal/a Enterprise plán", time:"pred 5 minútami", type:"order"},
                ].map((n, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded border border-gray-100">
                    <span className="text-xl">{n.flag}</span>
                    <div className="flex-1">
                      <span className="font-bold text-sm text-gray-800">{n.name}</span>
                      <span className="text-sm text-gray-500"> {n.msg}</span>
                    </div>
                    <span className="text-[10px] text-gray-400 font-mono">{n.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


        {/* === NOTIFICATIONS TAB === */}
        {activeTab === 'notifs' && (
          <div data-testid="admin-notifications-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Celkom</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{pushNotifs.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-red-200 p-4">
                <p className="text-xs font-medium text-red-500 uppercase">Neprečítané</p>
                <p className="text-3xl font-mono font-bold text-red-600 mt-1">{pushNotifs.unread || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-green-200 p-4">
                <p className="text-xs font-medium text-green-500 uppercase">Prečítané</p>
                <p className="text-3xl font-mono font-bold text-green-600 mt-1">{(pushNotifs.total || 0) - (pushNotifs.unread || 0)}</p>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button onClick={async () => {
                try { await axios.put(`${API}/push-notifications/read-all`); const r = await axios.get(`${API}/push-notifications?limit=50`); setPushNotifs(r.data); } catch {}
              }} className="px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded hover:bg-emerald-500" data-testid="admin-mark-all-read">
                Označiť všetky ako prečítané
              </button>
              <button onClick={async () => {
                try { await axios.delete(`${API}/push-notifications/clear-all`); const r = await axios.get(`${API}/push-notifications?limit=50`); setPushNotifs(r.data); } catch {}
              }} className="px-4 py-2 bg-white text-gray-600 text-sm font-medium rounded border border-gray-200 hover:bg-gray-50" data-testid="admin-clear-read">
                Vymazať prečítané
              </button>
              <button onClick={async () => {
                try { await axios.post(`${API}/push-notifications/test`); const r = await axios.get(`${API}/push-notifications?limit=50`); setPushNotifs(r.data); } catch {}
              }} className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded hover:bg-blue-500" data-testid="admin-test-notif">
                Testovacia notifikácia
              </button>
            </div>

            {/* Sound Selector */}
            <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6" data-testid="sound-selector">
              <div className="flex items-center gap-3 mb-3">
                <Volume2 className="w-5 h-5 text-gray-600" />
                <h4 className="font-bold text-sm text-gray-700 uppercase tracking-wider">Zvuk notifikácie</h4>
              </div>
              <p className="text-xs text-gray-500 mb-4">Vyberte zvuk, ktorý sa prehrá pri novej notifikácii. Kliknite na zvuk pre náhľad.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {NOTIFICATION_SOUNDS.map(s => (
                  <button
                    key={s.id}
                    data-testid={`sound-${s.id}`}
                    onClick={() => {
                      setNotifSound(s.id);
                      setStoredSound(s.id);
                      playNotificationSound(s.id);
                    }}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                      notifSound === s.id
                        ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    {s.id !== 'none' ? <Volume2 className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center text-gray-400">/</span>}
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 uppercase text-xs">Typ</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 uppercase text-xs">Správa</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 uppercase text-xs">Čas</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 uppercase text-xs">Stav</th>
                  </tr>
                </thead>
                <tbody>
                  {(pushNotifs.notifications || []).map((n, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${!n.read ? 'bg-blue-50/40' : ''}`} data-testid={`admin-notif-row-${i}`}>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                          n.type === 'security_alert' ? 'bg-red-100 text-red-700' :
                          n.type === 'new_registration' ? 'bg-purple-100 text-purple-700' :
                          n.type === 'scan_completed' ? 'bg-emerald-100 text-emerald-700' :
                          n.type === 'payment_received' ? 'bg-amber-100 text-amber-700' :
                          n.type === 'new_lead' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{n.type?.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{n.title}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{n.message}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{n.created_at ? new Date(n.created_at).toLocaleString('sk-SK') : '-'}</td>
                      <td className="px-4 py-3">
                        {n.read ? (
                          <span className="text-xs text-gray-400">Prečítané</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-bold text-[#FF3333]"><span className="w-2 h-2 rounded-full bg-[#FF3333]" />Nové</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!pushNotifs.notifications || pushNotifs.notifications.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Žiadne notifikácie</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* === PULSEAI BRAND TAB === */}
        {activeTab === 'pulseai' && (
          <AdminPulseAITab />
        )}

        {/* === THEME/DESIGN TAB === */}
        {activeTab === 'theme' && (
          <AdminThemeTab />
        )}

        {/* === BRAND ASSETS TAB === */}
        {activeTab === 'brand' && (
          <div data-testid="admin-brand-panel">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">Brand Assets</h3>
            <p className="text-xs text-gray-500 mb-6">Uložené obrázky pre Facebook, sociálne siete a marketing. Kliknite pre stiahnutie.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "FB Profilová — Pulse", file: "fb_profile_pulse.png", size: "1024×1024", desc: "Heartbeat pulse línia, TopRankPulse brand" },
                { name: "FB Profilová — Arrow", file: "fb_profile_arrow.png", size: "1024×1024", desc: "Červená šípka s glow efektom" },
                { name: "FB Titulná fotka", file: "fb_cover.png", size: "1536×1024", desc: "Is Your Website Invisible to AI?" },
              ].map((asset, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden group" data-testid={`brand-asset-${i}`}>
                  <div className="aspect-square bg-gray-950 flex items-center justify-center overflow-hidden">
                    <img src={`${API}/brand-assets/${asset.file}`} alt={asset.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="p-4">
                    <p className="font-bold text-sm text-gray-800">{asset.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{asset.size} · PNG</p>
                    <p className="text-xs text-gray-400 mt-1">{asset.desc}</p>
                    <a href={`${API}/brand-assets/${asset.file}`} download={asset.file}
                      className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 bg-gray-900 text-white text-xs font-bold rounded hover:bg-gray-700 transition-colors" data-testid={`download-asset-${i}`}>
                      Stiahnuť
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* === USERS MANAGEMENT TAB === */}
        {activeTab === 'users' && <AdminUsersPanel />}

        {/* === PRICING & EXCHANGE RATES TAB === */}
        {activeTab === 'pricing' && <AdminPricingTab />}

        {/* === E-SHOP SEO/GEO TIERS + ADDONS === */}
        {activeTab === 'eshop_tiers' && <AdminEshopTiersTab />}

        {activeTab === 'behavior' && <AdminBehaviorTab />}
        {activeTab === 'invoicing' && <AdminInvoicingTab />}
        {activeTab === 'ad_studio' && <AdminAdStudioTab />}
        {activeTab === 'ad_creative_studio' && <AdminAdCreativeStudioTab />}
        {activeTab === 'launch_checklist' && <AdminLaunchChecklistTab />}
        {activeTab === 'diagnostic_funnel' && <AdminDiagnosticFunnelTab />}
        {activeTab === 'manifest_ab' && <AdminManifestABTab />}
        {activeTab === 'hero_ab' && <AdminHeroABTab />}
        {activeTab === 'index_now' && <AdminIndexNowTab />}
        {activeTab === 'fb_ad_campaigns' && <AdminFbAdCampaignsTab />}
        {activeTab === 'challenge_5day' && <AdminChallengeTab />}

        {activeTab === 'fbig_ads' && <AdminAdsTab />}
        {activeTab === 'fb_ads_library' && <AdminFbAdsTab />}
        {activeTab === 'funnel' && <AdminFunnelTab />}

        {/* === REFERRAL TAB === */}
        {activeTab === 'referral' && <AdminReferralTab />}

        {/* === USERS MANAGEMENT TAB === */}
        {activeTab === 'users_mgmt' && <AdminUsersTab />}
        {activeTab === 'ab_stats' && <AdminABStatsTab />}
        {activeTab === 'drip_ab' && <AdminDripABTab />}
        {activeTab === 'cinematic_ads' && <AdminCinematicAdsTab />}
        {activeTab === 'visionary_copy' && <AdminVisionaryCopyTab />}
        {activeTab === 'seo_indexing' && <AdminSeoIndexingTab />}
        {activeTab === 'faqs' && <AdminFaqTab />}
        {activeTab === 'chat_feedback' && <AdminChatFeedbackTab />}
        {activeTab === 'agent_demand' && <AdminAgentDemandTab />}
        {activeTab === 'agents' && <AdminAgentsTab />}
        {activeTab === 'funnels' && <AdminFunnelsTab />}
        {activeTab === 'url_analytics' && <AdminUrlAnalyticsTab />}
        {activeTab === 'persona_ads' && <AdminPersonaAdsTab />}
        {activeTab === 'funnel_campaigns' && <AdminFunnelCampaignsTab />}
        <Toaster position="top-right" richColors />
        {activeTab === 'funnel_summary' && <AdminFunnelSummaryTab />}
        {activeTab === 'jendral_story' && <AdminJendralStoryTab />}
        {activeTab === 'case_studies' && <AdminCaseStudiesTab />}
        {activeTab === 'roadmap' && <AdminRoadmapTab />}
        {activeTab === 'anti_rankpill' && <AdminAntiRankPillTab />}
        {activeTab === 'leads_export' && <AdminLeadsExportTab />}
        {activeTab === 'gsc_setup' && <AdminGscSetupTab />}
        {activeTab === 'sitemap_ping' && <AdminSitemapPingTab />}
        {activeTab === 'seed_production' && <AdminSeedProductionTab />}
        {activeTab === 'studio_autopilot' && <AdminStudioAutopilotTab />}
        {activeTab === 'mika_ads' && <AdminMikaAdsTab />}


        {/* === WAITLIST TAB === */}
        {activeTab === 'waitlist' && (
          <div data-testid="admin-waitlist-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Celkom záujemcov</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{waitlist.total || 0}</p>
              </div>
              {Object.entries(waitlist.by_tool || {}).slice(0, 3).map(([slug, count]) => (
                <div key={slug} className="bg-white rounded-lg border border-purple-200 p-4">
                  <p className="text-xs font-medium text-purple-600 uppercase truncate">{slug}</p>
                  <p className="text-3xl font-mono font-bold text-purple-600 mt-1">{count}</p>
                </div>
              ))}
            </div>

            {/* Per-tool breakdown */}
            {Object.keys(waitlist.by_tool || {}).length > 0 && (
              <div className="flex gap-2 mb-4 flex-wrap">
                {Object.entries(waitlist.by_tool).map(([slug, count]) => (
                  <span key={slug} className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600">
                    {slug}: <span className="font-bold text-gray-900">{count}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Waitlist Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Zoznam záujemcov</h3>
                <button
                  type="button"
                  onClick={() => {
                    const rows = (waitlist.entries || []).map(e => ({
                      email: e.email, tool_slug: e.tool_slug,
                      language: e.language || '', created_at: e.created_at || '',
                    }));
                    if (!rows.length) return;
                    const csv = [Object.keys(rows[0]).join(','),
                      ...rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g,'""')}"`).join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'waitlist_export.csv'; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 hover:bg-gray-100 rounded"
                  data-testid="waitlist-export-btn"
                >
                  Export CSV
                </button>
              </div>
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
              ) : !waitlist.entries?.length ? (
                <div className="p-12 text-center text-gray-400">Zatiaľ žiadni záujemcovia. Ak niekto na <code>/tools</code> klikne na &bdquo;ČOSKORO&ldquo; kartu, objaví sa tu.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Email</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Nástroj</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">Jazyk</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Dátum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.entries.map((w, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`waitlist-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-gray-800">{w.email}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">{w.tool_slug}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-xs font-mono text-gray-500">{(w.language || '').toUpperCase()}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(w.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* === AFFILIATES TAB === */}
        {activeTab === 'affiliates' && (
          <div data-testid="admin-affiliates-panel">
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Partneri</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1" data-testid="aff-stat-count">
                  {affiliatesData.totals.count || 0}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-yellow-200 p-4">
                <p className="text-xs font-medium text-yellow-600 uppercase">Celkom zarobené</p>
                <p className="text-3xl font-mono font-bold text-yellow-600 mt-1" data-testid="aff-stat-earned">
                  €{(affiliatesData.totals.earned_eur || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-orange-200 p-4">
                <p className="text-xs font-medium text-orange-600 uppercase">Čaká na výplatu</p>
                <p className="text-3xl font-mono font-bold text-orange-600 mt-1" data-testid="aff-stat-pending">
                  €{(affiliatesData.totals.pending_eur || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-green-200 p-4">
                <p className="text-xs font-medium text-green-600 uppercase">Vyplatené</p>
                <p className="text-3xl font-mono font-bold text-green-600 mt-1" data-testid="aff-stat-paid">
                  €{(affiliatesData.totals.paid_eur || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Top 3 Leaderboard */}
            {affiliatesData.affiliates.length > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-xs font-bold text-yellow-700 uppercase tracking-wider mb-3">🏆 Top 3 partneri</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {affiliatesData.affiliates.slice(0, 3).map((a, i) => (
                    <div key={a.code} className="bg-white rounded-lg p-3 border border-yellow-300" data-testid={`aff-leaderboard-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 rounded-full bg-yellow-400 text-white font-black text-xs flex items-center justify-center">{i + 1}</span>
                        <span className="font-bold text-sm truncate text-gray-900">{a.name}</span>
                      </div>
                      <p className="text-xs font-mono text-gray-500 mb-2">/r/{a.code}</p>
                      <p className="text-xl font-mono font-bold text-yellow-600">€{(a.total_earned_eur || 0).toFixed(2)}</p>
                      <p className="text-[10px] text-gray-500">{a.total_conversions || 0} konverzií · {a.total_clicks || 0} klikov</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending payouts — quick action zone */}
            {affiliatesData.conversions.length > 0 && (
              <div className="bg-white rounded-lg border-2 border-orange-200 mb-6 overflow-hidden">
                <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
                  <h3 className="text-sm font-bold text-orange-700 uppercase tracking-wider">
                    Čaká na vyplatenie ({affiliatesData.conversions.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="aff-pending-table">
                    <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase">
                      <tr>
                        <th className="text-left px-4 py-2.5">Partner</th>
                        <th className="text-left px-4 py-2.5">Zákazník</th>
                        <th className="text-right px-4 py-2.5">Suma</th>
                        <th className="text-right px-4 py-2.5">Provízia</th>
                        <th className="text-right px-4 py-2.5">Dátum</th>
                        <th className="text-center px-4 py-2.5">Akcia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliatesData.conversions.map((c, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`aff-pending-row-${i}`}>
                          <td className="px-4 py-3 font-mono text-purple-700">/r/{c.code}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-600">{c.customer_email}</td>
                          <td className="px-4 py-3 text-right">€{(c.amount_eur || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-bold text-yellow-600">€{(c.commission_eur || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-xs text-gray-500">{(c.created_at || '').slice(0, 10)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const adminEmail = JSON.parse(localStorage.getItem("user") || "{}").email || "admin@toprankpulse.com";
                                  const headers = { "X-User-Email": adminEmail };
                                  await axios.post(`${API}/admin/affiliate-payouts/mark-paid`, {
                                    code: c.code, transaction_ids: [c.transaction_id],
                                  }, { headers });
                                  const r = await axios.get(`${API}/admin/affiliates?limit=200`, { headers });
                                  const r2 = await axios.get(`${API}/admin/affiliate-conversions?status=pending&limit=200`, { headers });
                                  setAffiliatesData({
                                    affiliates: r.data?.affiliates || [],
                                    conversions: r2.data?.conversions || [],
                                    totals: r.data?.totals || { count: 0, pending_eur: 0, paid_eur: 0, earned_eur: 0 },
                                  });
                                } catch (e) { alert('Chyba pri označení'); }
                              }}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase rounded"
                              data-testid={`aff-mark-paid-${i}`}
                            >
                              Vyplatiť
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Full partner table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Všetci partneri</h3>
              </div>
              {affiliatesData.affiliates.length === 0 ? (
                <p className="p-10 text-center text-sm text-gray-400">
                  Zatiaľ žiadny affiliate partner. Pošli záujemcov na <code className="bg-gray-100 px-1.5 py-0.5 rounded text-purple-600">/affiliate</code>
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="aff-partners-table">
                    <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase">
                      <tr>
                        <th className="text-left px-4 py-2.5">Partner</th>
                        <th className="text-left px-4 py-2.5">Kód</th>
                        <th className="text-right px-4 py-2.5">Kliky</th>
                        <th className="text-right px-4 py-2.5">Konverzie</th>
                        <th className="text-right px-4 py-2.5">Zarobené</th>
                        <th className="text-right px-4 py-2.5">Pending</th>
                        <th className="text-right px-4 py-2.5">Vyplatené</th>
                        <th className="text-center px-4 py-2.5">Výplata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliatesData.affiliates.map((a, i) => (
                        <tr key={a.code} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`aff-partner-row-${i}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{a.name}</p>
                            <p className="text-[10px] text-gray-500 font-mono">{a.email}</p>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            <span className={`px-2 py-0.5 rounded ${a.is_vanity ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                              /r/{a.code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">{a.total_clicks || 0}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{a.total_conversions || 0}</td>
                          <td className="px-4 py-3 text-right font-bold text-yellow-600">€{(a.total_earned_eur || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-orange-600">€{(a.pending_eur || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-green-600">€{(a.paid_eur || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-center text-[10px] text-gray-500">
                            {a.payout_method?.toUpperCase()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* === SECURITY TAB === */}
        {activeTab === 'security' && (
          <div data-testid="admin-security-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Celkom udalostí</p>
                <p className="text-3xl font-mono font-bold text-gray-900 mt-1">{securityEvents.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-emerald-200 p-4">
                <p className="text-xs font-medium text-emerald-600 uppercase">Ochrana</p>
                <p className="text-lg font-bold text-emerald-700 mt-1">Aktívna</p>
                <p className="text-xs text-emerald-500">Rate Limit + Brute Force + Hlavičky</p>
              </div>
              <div className="bg-white rounded-lg border border-blue-200 p-4">
                <p className="text-xs font-medium text-blue-600 uppercase">Bezpečnostné hlavičky</p>
                <p className="text-lg font-bold text-blue-700 mt-1">7 hlavičiek</p>
                <p className="text-xs text-blue-500">CSP, HSTS, XSS, Frame, MIME...</p>
              </div>
              <div className="bg-white rounded-lg border border-orange-200 p-4">
                <p className="text-xs font-medium text-orange-600 uppercase">Ochrana prihlásenia</p>
                <p className="text-lg font-bold text-orange-700 mt-1">5 pokusov</p>
                <p className="text-xs text-orange-500">15 min blokovanie po 5 neúspechoch</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Posledné bezpečnostné udalosti</h3>
              </div>
              {(securityEvents?.events?.length || 0) === 0 ? (
                <div className="p-12 text-center text-gray-400">Zatiaľ žiadne bezpečnostné udalosti. Ochrana je aktívna.</div>
              ) : (
                <table className="w-full">
                  <thead><tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <th className="px-4 py-2 text-left">Typ</th>
                    <th className="px-4 py-2 text-left">IP</th>
                    <th className="px-4 py-2 text-left">Závažnosť</th>
                    <th className="px-4 py-2 text-left">Detaily</th>
                    <th className="px-4 py-2 text-left">Čas</th>
                  </tr></thead>
                  <tbody>
                    {(securityEvents?.events || []).map((ev, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-medium">{ev.event_type}</td>
                        <td className="px-4 py-2 text-sm font-mono text-gray-500">{ev.ip_address}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${ev.severity === 'critical' ? 'bg-red-100 text-red-700' : ev.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                            {ev.severity}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500 max-w-[200px] truncate">{JSON.stringify(ev.details || {})}</td>
                        <td className="px-4 py-2 text-xs text-gray-400">{formatDate(ev.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("free");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const PLANS = [
    { id: "free", label: "Free", color: "bg-gray-100 text-gray-700", domains: 1 },
    { id: "pro", label: "Pro — €49/mes", color: "bg-blue-100 text-blue-700", domains: 5 },
    { id: "business", label: "Business — €169/mes", color: "bg-purple-100 text-purple-700", domains: 20 },
    { id: "enterprise", label: "Enterprise — €399/mes", color: "bg-yellow-100 text-yellow-700", domains: 999 },
  ];

  const ADDONS = [
    { id: "brand_monitor", label: "AI Brand Monitor", price: "€19/mes" },
    { id: "competitor_spy", label: "Competitor Spy", price: "€29/mes" },
    { id: "attribution", label: "AI Attribution", price: "€29/mes" },
    { id: "autopilot", label: "SEO Autopilot", price: "€79/mes" },
    { id: "fix_seo", label: "Fix My SEO", price: "€9 jednorazovo" },
    { id: "blog_agent", label: "Blog Agent (25 článkov)", price: "€25 jednorazovo" },
  ];

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/domains/admin/users`, { withCredentials: true });
        setUsers(res.data.users || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  const startEdit = (user) => {
    setEditingUser(user);
    setSelectedPlan(user.plan || "free");
    setSelectedAddons(user.addons || []);
    setMsg("");
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    setMsg("");
    try {
      await axios.post(`${API}/domains/admin/assign-plan`, {
        email: editingUser.email,
        plan: selectedPlan,
        addons: selectedAddons,
      }, { withCredentials: true });
      setUsers(prev => prev.map(u => u.email === editingUser.email ? { ...u, plan: selectedPlan, addons: selectedAddons } : u));
      setMsg("Plán úspešne priradený!");
      setTimeout(() => setEditingUser(null), 1500);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Chyba");
    }
    setSaving(false);
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => prev.includes(addonId) ? prev.filter(a => a !== addonId) : [...prev, addonId]);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  return (
    <div data-testid="admin-users-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Správa používateľov</h3>
          <p className="text-xs text-gray-500 mt-1">{users.length} používateľov · Priraďte plány a add-ony</p>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase">Používateľ</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase">Plán</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase">Add-ony</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase">Registrácia</th>
              <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase">Akcia</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => {
              const planInfo = PLANS.find(p => p.id === (u.plan || "free")) || PLANS[0];
              return (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${i}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{u.name || "—"}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-bold rounded ${planInfo.color}`}>{planInfo.label.split("—")[0].trim()}</span>
                  </td>
                  <td className="px-4 py-3">
                    {(u.addons || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {u.addons.map((a, j) => {
                          const addon = ADDONS.find(ad => ad.id === a);
                          return <span key={j} className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded">{addon?.label || a}</span>;
                        })}
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.created_at ? new Date(u.created_at).toLocaleDateString("sk-SK") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => startEdit(u)} className="px-3 py-1.5 bg-gray-900 text-white text-xs font-bold rounded hover:bg-gray-700" data-testid={`edit-user-${i}`}>
                      Upraviť
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-user-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold">Upraviť používateľa</h3>
                <p className="text-sm text-gray-500">{editingUser.email}</p>
              </div>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {/* Plan Selection */}
            <div className="mb-6">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Plán</label>
              <div className="grid grid-cols-2 gap-2">
                {PLANS.map(plan => (
                  <button key={plan.id} onClick={() => setSelectedPlan(plan.id)}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      selectedPlan === plan.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`} data-testid={`plan-select-${plan.id}`}>
                    <p className="font-bold text-sm">{plan.label}</p>
                    <p className="text-xs text-gray-500">{plan.domains === 999 ? "Neobmedzené" : plan.domains} domén</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Addons */}
            <div className="mb-6">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Platené funkcie (Add-ony)</label>
              <div className="space-y-2">
                {ADDONS.map(addon => (
                  <label key={addon.id}
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedAddons.includes(addon.id) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                    }`} data-testid={`addon-${addon.id}`}>
                    <input type="checkbox" checked={selectedAddons.includes(addon.id)} onChange={() => toggleAddon(addon.id)}
                      className="w-4 h-4 text-emerald-600 rounded border-gray-300" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{addon.label}</p>
                    </div>
                    <span className="text-xs font-bold text-gray-500">{addon.price}</span>
                  </label>
                ))}
              </div>
            </div>

            {msg && (
              <div className={`p-3 rounded-lg text-sm font-medium mb-4 ${msg.includes("úspešne") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {msg}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-user-plan">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Uložiť zmeny
              </button>
              <button onClick={() => setEditingUser(null)} className="px-6 py-3 border border-gray-300 text-gray-600 font-medium rounded-lg hover:bg-gray-50">
                Zrušiť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
